import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, ArrowRight, CheckCircle2, MapPin, ChevronRight } from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Contact } from "../components/site/Contact";
import { WhatsAppFloat } from "../components/site/WhatsAppFloat";
import { LOCAL_PAGES, getLocalPage } from "../lib/localPages";
import { waLink, BRAND } from "../lib/data";

export default function LocalLanding() {
  const { slug } = useParams();
  const page = getLocalPage(slug);

  useEffect(() => {
    if (!page) return;
    document.title = page.metaTitle;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", page.metaDesc);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = `https://satcom.id/layanan/${page.slug}`;
    window.scrollTo(0, 0);
  }, [page]);

  if (!page) return <Navigate to="/" replace />;

  const related = LOCAL_PAGES.filter((p) => p.slug !== page.slug).slice(0, 4);

  return (
    <div className="bg-white text-[#111827] antialiased overflow-x-hidden">
      <Navbar />
      <header className="relative overflow-hidden bg-[#071A33] pt-[110px] pb-20">
        <div className="absolute inset-0 opacity-70 [background:radial-gradient(60%_50%_at_15%_10%,rgba(11,99,246,0.35),transparent_60%),radial-gradient(50%_50%_at_90%_0%,rgba(22,199,232,0.25),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <nav className="flex items-center gap-2 text-sm text-white/60">
            <Link to="/" className="hover:text-white">Beranda</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white/90">{page.h1}</span>
          </nav>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="mt-5 font-heading font-extrabold text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-3xl"
          >
            {page.h1}
          </motion.h1>
          <p className="mt-5 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed">{page.intro}</p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <a href={waLink(page.wa)} target="_blank" rel="noopener noreferrer" data-testid="local-wa-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold px-7 py-4 transition-colors shadow-xl shadow-[#25D366]/30 hover:scale-[1.03]">
              <MessageCircle className="h-5 w-5" /> Konsultasi via WhatsApp
            </a>
            <a href="/#produk" data-testid="local-produk-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur text-white font-semibold px-7 py-4 hover:bg-white/10 transition-colors">
              Lihat Produk & Harga <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm text-white/60">
            <MapPin className="h-4 w-4 text-[#16C7E8]" /> Melayani {BRAND.area}
          </p>
        </div>
      </header>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {page.points.map((pt, i) => (
              <motion.div key={pt.t}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
                className="rounded-2xl border border-gray-100 bg-white p-7 shadow-sm hover:shadow-xl transition-shadow">
                <CheckCircle2 className="h-6 w-6 text-[#0B63F6]" />
                <h3 className="mt-4 font-heading font-bold text-lg text-[#111827]">{pt.t}</h3>
                <p className="mt-1.5 text-sm text-[#6B7280] leading-relaxed">{pt.d}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16">
            <h2 className="font-heading font-bold text-2xl text-[#111827]">Layanan Lainnya di Rangkasbitung & Lebak</h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/layanan/${r.slug}`} data-testid={`related-${r.slug}`}
                  className="group flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-[#F5F7FA]/60 px-5 py-4 hover:bg-white hover:shadow-md transition-all">
                  <span className="font-semibold text-sm text-[#111827]">{r.h1}</span>
                  <ArrowRight className="h-4 w-4 text-[#0B63F6] group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Contact />
      <WhatsAppFloat />
    </div>
  );
}
